import { notFound, redirect } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { PlacesNotFoundError } from "common/apis/places";
import { placesPageQueryOptions } from "common/hooks/places/placesQueryOptions";
import type { PlacesCityParams, PlacesCityResponse } from "common/types/places";
import { placesSearch, singlePageType } from "@/lib/placesPage";

/**
 * SSR loader for every /places/<city>[/<area>][/<type>] page.
 *
 * @param queryClient Router context query client.
 * @param citySlug City slug.
 * @param segments None, the type, the area, or the area then the type.
 * @param search Page and filters from the URL.
 * @returns The page data, the search and the canonical origin.
 */
export async function loadPlacesPage(
  queryClient: QueryClient,
  citySlug: string,
  segments: string[],
  search: PlacesCityParams,
) {
  const canonicalOrigin =
    (typeof process !== "undefined" && process.env?.PUBLIC_ORIGIN) || "";
  let response: PlacesCityResponse;
  try {
    response = await queryClient.ensureQueryData(placesPageQueryOptions(citySlug, segments, search));
  } catch (err) {
    if (err instanceof PlacesNotFoundError) throw notFound();
    // PlacesUnavailableError propagates: a search outage is a 5xx, never a 404 or an empty page.
    throw err;
  }

  const path = `/places/${[citySlug, ...segments].join("/")}`;
  // Both redirects list the same venues as the URL they replace, so the page number carries over:
  // the API ignores `types` on a type page.
  if (response.data.type) {
    if (search.types) {
      throw redirect({ to: path, search: placesSearch({ ...search, types: undefined }), statusCode: 301 });
    }
  } else {
    const pageType = singlePageType(search.types, response.data.page_types);
    if (pageType) {
      throw redirect({ to: `${path}/${pageType.slug}`, search: placesSearch({ ...search, types: undefined }), statusCode: 301 });
    }
  }

  return { page: response.data, search, canonicalOrigin };
}
